import { createContext, useContext } from 'react';
import { Platform } from 'react-native';

export enum ScreenType {
    /**
     * The react native screen is rendered in a full screen context.
     * This means that we are responsible for rendering certain navigation components such as the back button.
     */
    FULL_SCREEN = 'FULL_SCREEN',
    /**
     * The react native screen is rendered in a bottom sheet, modal experience.
     */
    BOTTOM_SHEET = 'BOTTOM_SHEET',
    /**
     * The screen is rendered within a pane on a desktop layout
     */
    EMBEDDED_PANE = 'EMBEDDED_PANE',
}

export default ScreenType;

const ScreenTypeContext = createContext<ScreenType | null>(null);

export function ScreenTypeProvider({
    screenType,
    children,
}: {
    screenType: ScreenType;
    children: React.ReactNode;
}) {
    return <ScreenTypeContext.Provider value={screenType}>{children}</ScreenTypeContext.Provider>;
}

export function useScreenType() {
    const screenType = useContext(ScreenTypeContext);
    if (!screenType) {
        throw new Error('ScreenTypeProvider not set');
    }
    return screenType;
}

export function useIsFullScreen() {
    return useScreenType() === ScreenType.FULL_SCREEN;
}

export function useIsBottomSheet() {
    return useScreenType() === ScreenType.BOTTOM_SHEET;
}

export function isWebBottomSheet(screenType: ScreenType): boolean {
    return Platform.OS === 'web' && screenType === ScreenType.BOTTOM_SHEET;
}

export function useIsWebBottomSheet() {
    return isWebBottomSheet(useScreenType());
}

export function useIsEmbeddedPane() {
    return useScreenType() === ScreenType.EMBEDDED_PANE;
}
